import {
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  categoriesFor,
  describeError,
  dirhams,
  exportFilter,
  FILTER_KEYS,
  fullDate,
  groupByPatch,
  type OperationType,
  type OperationValues,
  operationsCount,
  operationsFilter,
  present,
  TYPE_LABELS,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Dropdown,
  Input,
  type InputRef,
  Modal,
  Select,
  Table,
  type TableProps,
  Tag,
  Tooltip,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useClient, useQuery } from 'urql';
import { BulkChange } from './BulkChange';
import {
  bulkError,
  FinanceExportLinkDocument,
  financeContext,
  type Lists,
  lockReason,
  OperationsTableDocument,
  type TableOperation,
  UpdateOperationsDocument,
  useCanWrite,
  useLists,
} from './data';
import { OperationPanel } from './OperationPanel';
import { PeriodFilter } from './PeriodFilter';
import { CategoryPicker, CounterpartyPicker, ProjectPicker } from './pickers';
import { ListHeader } from './ui';

const SORTS = {
  'date-desc': ['DATE_DESC', 'CREATED_AT_DESC'],
  'date-asc': ['DATE_ASC', 'CREATED_AT_ASC'],
  'amount-desc': ['AMOUNT_DESC', 'DATE_DESC'],
  'amount-asc': ['AMOUNT_ASC', 'DATE_DESC'],
} as const;
type Sort = keyof typeof SORTS;

const PAGE_SIZES = [25, 50, 100];

/** The list's title: the menu's view (doc 63). */
function viewTitle(params: URLSearchParams): string {
  if (params.get('review')) return 'تحتاج مراجعة';
  switch (params.get('missing')) {
    case 'category':
      return 'بلا صنف';
    case 'counterparty':
      return 'بلا جهة';
    case 'receipt':
      return 'مصاريف بلا وصل';
    default:
      return 'العمليات';
  }
}

/** Keys typed in a field are the field's, not the table's. */
const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  !!target.closest(
    'input, textarea, [contenteditable="true"], .ant-select, .ant-modal, .ant-picker',
  );

/** What a table cell can change. Saved only after the summary (`Changes`). */
export type Draft = {
  categoryId?: string | null;
  counterpartyId?: string | null;
  projectId?: string | null;
  needsReview?: boolean;
  /** null clears it (the review flag removed). */
  reviewNote?: string | null;
};
type Drafts = Record<string, { op: TableOperation; patch: Draft }>;

const FIELD_LABELS: Record<keyof Draft, string> = {
  categoryId: 'الصنف',
  counterpartyId: 'الجهة',
  projectId: 'المشروع',
  needsReview: 'تحتاج مراجعة',
  reviewNote: 'ملاحظة المراجعة',
};

/** A table draft as the panel form's values (a cleared note is empty). */
function formDraft(
  patch: Draft | undefined,
): Partial<OperationValues> | undefined {
  if (!patch) return undefined;
  const { reviewNote, ...rest } = patch;
  return 'reviewNote' in patch
    ? { ...rest, reviewNote: reviewNote ?? '' }
    : rest;
}

/** A draft without the fields set back to their saved value. */
export function cleanDraft(
  saved: Partial<Record<keyof Draft, unknown>>,
  patch: Draft,
): Draft {
  const out: Draft = { ...patch };
  for (const key of Object.keys(out) as (keyof Draft)[]) {
    if (out[key] === (saved[key] ?? null)) delete out[key];
  }
  return out;
}

/**
 * Columns by priority: the first ones always, the others as the width
 * allows (the description keeps at least 200 px). Widths in px.
 */
const COLUMN_WIDTHS = {
  date: 96,
  description: 200,
  amount: 124,
  review: 84,
  receipt: 64,
  category: 140,
  counterparty: 150,
  project: 160,
  account: 120,
} as const;
type ColumnKey = keyof typeof COLUMN_WIDTHS;
const ALWAYS: ColumnKey[] = ['date', 'description', 'amount', 'review'];
const OPTIONAL: ColumnKey[] = [
  'category',
  'counterparty',
  'project',
  'receipt',
  'account',
];

/** The columns that fit in `width` px, in priority order. */
export function visibleColumns(width: number): Set<ColumnKey> {
  const shown = new Set<ColumnKey>(ALWAYS);
  // Scroll bar and borders.
  let used = ALWAYS.reduce((sum, k) => sum + COLUMN_WIDTHS[k], 0) + 40;
  for (const key of OPTIONAL) {
    if (used + COLUMN_WIDTHS[key] > width) break;
    shown.add(key);
    used += COLUMN_WIDTHS[key];
  }
  return shown;
}

function useWidth(ref: React.RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState(1000);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/**
 * The operations as a table: filters and views in the address (bookmarkable),
 * sorting and pages run on the server, the main fields changed in their cells
 * (kept aside until «مراجعة وحفظ» and its summary), ticked rows changed
 * together («تغيير المحدَّد»), and the open row's full form in the side panel.
 */
export function OperationsScreen() {
  const canWrite = useCanWrite();
  const { modal } = App.useApp();
  const [params, setParams] = useSearchParams();
  const searchRef = useRef<InputRef>(null);
  const listRef = useRef<HTMLElement>(null);
  const width = useWidth(listRef);
  const [cursor, setCursor] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [reviewing, setReviewing] = useState(false);
  // Rows ticked for «تغيير المحدَّد», kept across pages and filters.
  const [selected, setSelected] = useState<Record<string, TableOperation>>({});
  const [bulk, setBulk] = useState(false);
  const panelDirty = useRef(false);
  const onDirtyChange = useCallback((dirty: boolean) => {
    panelDirty.current = dirty;
  }, []);
  const draftCount = Object.keys(drafts).length;

  // Unsaved table changes: the browser asks before leaving the page.
  useEffect(() => {
    if (!draftCount) return;
    const onLeave = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [draftCount]);

  const update = useCallback(
    (patch: Record<string, string | null>, resetPage = true) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const page = Number(params.get('page') ?? 1) || 1;
  const size = PAGE_SIZES.includes(Number(params.get('size')))
    ? Number(params.get('size'))
    : 50;
  const sortParam = params.get('sort');
  const sort: Sort =
    sortParam && sortParam in SORTS ? (sortParam as Sort) : 'date-desc';
  const panel = params.get('panel');
  const type = params.get('type') as OperationType | null;

  const filter = useMemo(() => operationsFilter(params), [params]);
  const [{ data: lists }] = useLists();
  const [{ data, error, fetching }] = useQuery({
    query: OperationsTableDocument,
    variables: {
      filter,
      orderBy: [...SORTS[sort]],
      first: size,
      offset: (page - 1) * size,
    },
    context: useMemo(() => financeContext, []),
  });
  const rows = present(data?.financialOperations?.nodes);
  const total = data?.financialOperations?.totalCount ?? 0;
  // The selected rows as last loaded (a row on this page is fresher).
  const selectedRows = Object.values(selected).map(
    (op) => rows.find((r) => r.id === op.id) ?? op,
  );

  /** Run `action` now, or after confirming the panel's edits are dropped. */
  const guard = useCallback(
    async (action: () => void) => {
      if (panelDirty.current) {
        const ok = await modal.confirm({
          title: 'تعديلات غير محفوظة',
          content: 'في اللوحة تعديلات لم تُحفظ. هل تريد تجاهلها؟',
          okText: 'تجاهلها',
          okButtonProps: { danger: true },
          cancelText: 'رجوع',
        });
        if (!ok) return;
        panelDirty.current = false;
      }
      action();
    },
    [modal],
  );

  const open = useCallback(
    // `copy`: «نسخ» of an operation. Not `from`, the period's start (doc 59).
    (id: string | null, copy?: string) =>
      guard(() =>
        update(
          { panel: id, copy: copy ?? null, debt: null, optype: null },
          false,
        ),
      ),
    [guard, update],
  );

  const neighbour = useCallback(
    (id: string | null, step: 1 | -1) => {
      const i = rows.findIndex((r) => r.id === id);
      return rows[i === -1 ? 0 : i + step]?.id ?? null;
    },
    [rows],
  );

  const moveTo = useCallback(
    (id: string | null) => {
      if (!id) return;
      const go = () => {
        setCursor(id);
        if (panel && panel !== 'new') update({ panel: id, copy: null }, false);
        document
          .querySelector(`[data-row-key="${id}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      };
      if (panel && panel !== 'new') guard(go);
      else go();
    },
    [panel, guard, update],
  );

  // A change back to the saved value is no change.
  const setDraft = useCallback((op: TableOperation, patch: Draft) => {
    setDrafts((all) => {
      const merged = cleanDraft(op, { ...all[op.id]?.patch, ...patch });
      const next = { ...all };
      if (Object.keys(merged).length) next[op.id] = { op, patch: merged };
      else delete next[op.id];
      return next;
    });
  }, []);

  const dropDraft = useCallback((id: string) => {
    setDrafts((all) => {
      if (!all[id]) return all;
      const next = { ...all };
      delete next[id];
      return next;
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      // Ctrl+S with the panel closed: review and save the table's changes.
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyS' && !panel) {
        e.preventDefault();
        if (draftCount) setReviewing(true);
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      const at = panel && panel !== 'new' ? panel : cursor;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        moveTo(
          at ? neighbour(at, e.key === 'ArrowDown' ? 1 : -1) : rows[0]?.id,
        );
      } else if (e.key === 'Enter' && at) {
        e.preventDefault();
        open(at);
      } else if (e.code === 'Slash') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.code === 'KeyN' && canWrite) {
        e.preventDefault();
        open('new');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panel, cursor, rows, neighbour, moveTo, open, canWrite, draftCount]);

  // The selection's check boxes take 32 px.
  const shown = visibleColumns(canWrite ? width - 32 : width);
  const columns = buildColumns({
    shown,
    sort,
    canWrite,
    drafts,
    setDraft,
    openId: panel,
  });
  const hiddenCount = OPTIONAL.filter((k) => !shown.has(k)).length;

  const onTableChange: TableProps<TableOperation>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = s?.columnKey === 'amount' ? 'amount' : 'date';
    const nextSort: Sort = s?.order
      ? (`${field}-${s.order === 'ascend' ? 'asc' : 'desc'}` as Sort)
      : 'date-desc';
    update(
      {
        sort: nextSort === 'date-desc' ? null : nextSort,
        size: pagination.pageSize === 50 ? null : String(pagination.pageSize),
        page:
          nextSort !== sort || pagination.current === 1
            ? null
            : String(pagination.current),
      },
      false,
    );
  };

  return (
    <div className="desk-split">
      <section className="desk-list" ref={listRef}>
        <ListHeader
          title={viewTitle(params)}
          count={data ? total : undefined}
          description={
            <>
              <span>
                تُعدَّل الخانات في الجدول ثم «مراجعة وحفظ»، وتُفتح العملية فوق
                القائمة.
              </span>
              {hiddenCount > 0 && (
                <span>
                  بعض الأعمدة مخفية لضيق الشاشة، وتظهر كلها في العملية.
                </span>
              )}
            </>
          }
          actions={
            <>
              <ExportButton params={params} />
              {canWrite && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => open('new')}
                >
                  عملية جديدة (N)
                </Button>
              )}
            </>
          }
        />
        <div className="ui-table-card">
          <div className="desk-toolbar">
            <PeriodFilter
              // Cleared by «إلغاء التصفية»: back to «الكل».
              key={params.get('from') || params.get('to') ? 'set' : 'none'}
              from={params.get('from')}
              to={params.get('to')}
              onChange={(from, to) => update({ from, to })}
            />
            <Input
              ref={searchRef}
              allowClear
              prefix={<SearchOutlined />}
              placeholder="بحث في البيان ( / )"
              className="desk-search"
              value={params.get('q') ?? ''}
              onChange={(e) => update({ q: e.target.value })}
            />
            <Select<OperationType>
              allowClear
              placeholder="النوع"
              className="desk-filter"
              value={type ?? undefined}
              onChange={(v) => update({ type: v ?? null })}
              options={[
                { value: 'credit', label: 'المداخيل' },
                { value: 'debit', label: 'المصاريف' },
              ]}
            />
            <Select
              allowClear
              placeholder="الحساب"
              className="desk-filter"
              value={params.get('account') ?? undefined}
              onChange={(v) => update({ account: v ?? null })}
              options={present(lists?.financialAccounts?.nodes).map((a) => ({
                value: a.id,
                label: a.name,
              }))}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="المشروع"
              className="desk-filter is-wide"
              value={params.get('project') ?? undefined}
              onChange={(v) => update({ project: v ?? null })}
              options={present(lists?.projects?.nodes).map((p) => ({
                value: p.id,
                label: p.status === 'CLOSED' ? `${p.name} (مغلق)` : p.name,
              }))}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="الصنف"
              className="desk-filter"
              value={params.get('category') ?? undefined}
              onChange={(v) => update({ category: v ?? null })}
              options={categoriesFor(
                present(lists?.operationCategories?.nodes),
                type,
              ).map((c) => ({ value: c.id, label: c.name }))}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="الجهة"
              className="desk-filter"
              value={params.get('counterparty') ?? undefined}
              onChange={(v) => update({ counterparty: v ?? null })}
              options={present(lists?.counterparties?.nodes).map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
            {FILTER_KEYS.some((k) => params.get(k)) && (
              <Button
                type="link"
                onClick={() =>
                  update(Object.fromEntries(FILTER_KEYS.map((k) => [k, null])))
                }
              >
                إلغاء التصفية
              </Button>
            )}
          </div>

          {selectedRows.length > 0 && (
            <div className="desk-drafts is-selection" role="status">
              <span>
                <strong className="ltr-num">{selectedRows.length}</strong>{' '}
                {selectedRows.length === 1 ? 'عملية مختارة' : 'عمليات مختارة'}
              </span>
              <Button type="primary" onClick={() => setBulk(true)}>
                تغيير المحدَّد
              </Button>
              <Button onClick={() => setSelected({})}>إلغاء الاختيار</Button>
            </div>
          )}

          {draftCount > 0 ? (
            <div className="desk-drafts" role="status">
              <span>
                <strong className="ltr-num">{draftCount}</strong>{' '}
                {draftCount === 1 ? 'عملية معدَّلة' : 'عمليات معدَّلة'} في الجدول،
                لم تُحفظ بعد.
              </span>
              <Button type="primary" onClick={() => setReviewing(true)}>
                مراجعة وحفظ (Ctrl+S)
              </Button>
              <Button
                onClick={async () => {
                  const ok = await modal.confirm({
                    title: 'التراجع عن التعديلات',
                    content: 'تُلغى كل تعديلات الجدول التي لم تُحفظ.',
                    okText: 'تراجع',
                    okButtonProps: { danger: true },
                    cancelText: 'رجوع',
                  });
                  if (ok) setDrafts({});
                }}
              >
                تراجع
              </Button>
            </div>
          ) : null}

          {error && (
            <Alert
              type="error"
              showIcon
              message={describeError(error, 'تعذّر تحميل العمليات.')}
            />
          )}

          <Table<TableOperation>
            rowKey="id"
            size="small"
            tableLayout="fixed"
            columns={columns}
            dataSource={rows}
            loading={fetching && !data}
            scroll={{ y: 'calc(100vh - 250px)' }}
            onChange={onTableChange}
            rowSelection={
              canWrite
                ? {
                    columnWidth: 32,
                    preserveSelectedRowKeys: true,
                    selectedRowKeys: Object.keys(selected),
                    getCheckboxProps: (op) => ({
                      disabled: !!lockReason(op, canWrite),
                    }),
                    onChange: (keys, picked) =>
                      setSelected((prev) =>
                        Object.fromEntries(
                          keys.flatMap((k) => {
                            const op =
                              picked.find((r) => r?.id === k) ??
                              prev[String(k)];
                            return op ? [[String(k), op]] : [];
                          }),
                        ),
                      ),
                  }
                : undefined
            }
            sortDirections={['descend', 'ascend']}
            showSorterTooltip={false}
            pagination={{
              current: page,
              pageSize: size,
              total,
              size: 'small',
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZES,
              showTotal: (n, [a, b]) => `${a}–${b} من ${n}`,
            }}
            rowClassName={(op) =>
              [
                `is-${op.type}`,
                op.id === panel ? 'is-open' : '',
                op.id === cursor && op.id !== panel ? 'is-cursor' : '',
                drafts[op.id] ? 'is-changed' : '',
              ].join(' ')
            }
            onRow={(op) => ({
              onClick: (e) => {
                // The check box ticks the row, it doesn't open it.
                if (
                  (e.target as HTMLElement).closest(
                    '.ant-table-selection-column',
                  )
                )
                  return;
                setCursor(op.id);
                open(op.id);
              },
            })}
            locale={{ emptyText: 'لا عمليات بهذا الاختيار' }}
          />
        </div>
      </section>

      {panel && (
        <OperationPanel
          key={`${panel}:${params.get('copy') ?? ''}:${params.get('debt') ?? ''}`}
          id={panel === 'new' ? null : panel}
          copyFrom={params.get('copy')}
          debtId={panel === 'new' ? params.get('debt') : null}
          presetType={
            panel === 'new'
              ? (params.get('optype') as OperationType | null)
              : null
          }
          draft={panel !== 'new' ? formDraft(drafts[panel]?.patch) : undefined}
          onSaved={dropDraft}
          onDirtyChange={onDirtyChange}
          onClose={() => {
            if (panel !== 'new') setCursor(panel);
            open(null);
          }}
          onOpen={(id) => {
            setCursor(id);
            update({ panel: id, copy: null, debt: null, optype: null }, false);
          }}
          onNew={(copy) => open('new', copy)}
          nextId={panel === 'new' ? null : neighbour(panel, 1)}
        />
      )}

      {bulk && (
        <BulkChange
          rows={selectedRows}
          lists={lists}
          onDone={() => {
            setBulk(false);
            setSelected({});
          }}
          onClose={() => setBulk(false)}
        />
      )}

      {reviewing && (
        <Changes
          drafts={drafts}
          lists={lists}
          onSaved={dropDraft}
          onClose={() => setReviewing(false)}
        />
      )}
    </div>
  );
}

function buildColumns({
  shown,
  sort,
  canWrite,
  drafts,
  setDraft,
  openId,
}: {
  /** The row open in the panel: edited there, not in its cells. */
  openId: string | null;
  shown: Set<ColumnKey>;
  sort: Sort;
  canWrite: boolean;
  drafts: Drafts;
  setDraft: (op: TableOperation, patch: Draft) => void;
}): NonNullable<TableProps<TableOperation>['columns']> {
  const sortOrder = (key: 'date' | 'amount') =>
    sort.startsWith(key)
      ? sort.endsWith('asc')
        ? ('ascend' as const)
        : ('descend' as const)
      : null;
  const fixed = (op: TableOperation) =>
    !!lockReason(op, canWrite) || op.id === openId;
  // A row's value with its pending change, and whether it changed.
  const cell = <K extends keyof Draft>(op: TableOperation, key: K) => {
    const patch = drafts[op.id]?.patch;
    const changed = !!patch && key in patch;
    const value = changed ? patch?.[key] : op[key as keyof TableOperation];
    return { value: (value ?? null) as Draft[K] | null, changed };
  };

  type Column = NonNullable<TableProps<TableOperation>['columns']>[number];
  const all: Record<ColumnKey, Column> = {
    date: {
      key: 'date',
      title: 'التاريخ',
      dataIndex: 'date',
      width: COLUMN_WIDTHS.date,
      sorter: true,
      sortOrder: sortOrder('date'),
      render: (date: string) => (
        <Tooltip title={fullDate(date)}>
          <span className="ltr-num">{date}</span>
        </Tooltip>
      ),
    },
    description: {
      key: 'description',
      title: 'البيان',
      render: (_, op) => <Description op={op} />,
    },
    category: {
      key: 'category',
      title: 'الصنف',
      width: COLUMN_WIDTHS.category,
      render: (_, op) => {
        if (fixed(op)) return op.category?.name;
        const { value, changed } = cell(op, 'categoryId');
        return (
          <CellBox>
            <CategoryPicker
              size="small"
              variant="borderless"
              placeholder="—"
              type={op.type}
              changed={changed}
              value={value as string | null}
              onChange={(categoryId) => setDraft(op, { categoryId })}
            />
          </CellBox>
        );
      },
    },
    counterparty: {
      key: 'counterparty',
      title: 'الجهة',
      width: COLUMN_WIDTHS.counterparty,
      render: (_, op) => {
        if (fixed(op)) return op.counterparty?.name;
        const { value, changed } = cell(op, 'counterpartyId');
        return (
          <CellBox>
            <CounterpartyPicker
              size="small"
              variant="borderless"
              placeholder="—"
              type={op.type}
              changed={changed}
              value={value as string | null}
              onChange={(counterpartyId) => setDraft(op, { counterpartyId })}
            />
          </CellBox>
        );
      },
    },
    project: {
      key: 'project',
      title: 'المشروع',
      width: COLUMN_WIDTHS.project,
      render: (_, op) => {
        if (fixed(op) || !op.projectId) return op.project?.name;
        const { value, changed } = cell(op, 'projectId');
        return (
          <CellBox>
            <ProjectPicker
              size="small"
              variant="borderless"
              keep={op.projectId}
              changed={changed}
              value={value as string | null}
              onChange={(projectId) => projectId && setDraft(op, { projectId })}
            />
          </CellBox>
        );
      },
    },
    account: {
      key: 'account',
      title: 'الحساب',
      width: COLUMN_WIDTHS.account,
      ellipsis: true,
      render: (_, op) => op.account?.name,
    },
    receipt: {
      key: 'receipt',
      title: 'وصل',
      width: COLUMN_WIDTHS.receipt,
      render: (_, op) =>
        op.receiptPhoto ? (
          op.receiptPhoto.contentType === 'application/pdf' ? (
            <FilePdfOutlined className="receipt-mark" aria-label="وصل PDF" />
          ) : (
            <FileImageOutlined className="receipt-mark" aria-label="وصل" />
          )
        ) : null,
    },
    review: {
      key: 'review',
      title: 'مراجعة',
      width: COLUMN_WIDTHS.review,
      render: (_, op) => {
        const { value, changed } = cell(op, 'needsReview');
        return (
          <CellBox>
            <Checkbox
              aria-label="تحتاج مراجعة"
              className={changed ? 'is-changed' : undefined}
              checked={!!value}
              disabled={fixed(op)}
              onChange={(e) =>
                setDraft(op, {
                  needsReview: e.target.checked,
                  // Unticked: the note goes with the flag.
                  reviewNote: e.target.checked ? op.reviewNote : null,
                })
              }
            />
          </CellBox>
        );
      },
    },
    amount: {
      key: 'amount',
      title: 'المبلغ',
      dataIndex: 'amount',
      width: COLUMN_WIDTHS.amount,
      sorter: true,
      sortOrder: sortOrder('amount'),
      render: (_, op) => (
        <span className={`desk-amount is-${op.type}`}>
          {op.type === 'credit' ? '+' : '−'} {dirhams(op.amount)}
        </span>
      ),
    },
  };
  // Reading order: date, what, details, money.
  const order: ColumnKey[] = [
    'date',
    'description',
    'category',
    'counterparty',
    'project',
    'account',
    'receipt',
    'review',
    'amount',
  ];
  return order.filter((k) => shown.has(k)).map((k) => all[k]);
}

/**
 * «تصدير»: the operations of the current filters as Excel or CSV (arqam
 * builds the file; the link opens it). Filters it can't carry are named.
 */
function ExportButton({ params }: { params: URLSearchParams }) {
  const client = useClient();
  const { message, modal } = App.useApp();
  const [busy, setBusy] = useState(false);

  async function run(format: 'XLSX' | 'CSV') {
    const { filter, skipped } = exportFilter(params);
    if (skipped.length) {
      const ok = await modal.confirm({
        title: 'تصدير العمليات',
        content: `لا يأخذ الملف بعين الاعتبار: ${skipped.join('، ')}. تُصدَّر العمليات حسب باقي التصفية.`,
        okText: 'تصدير',
        cancelText: 'رجوع',
      });
      if (!ok) return;
    }
    // Opened at the click: browsers block a window opened later.
    const tab = window.open('', '_blank');
    setBusy(true);
    const r = await client
      .query(
        FinanceExportLinkDocument,
        { filter, format },
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    setBusy(false);
    const link = r.data?.financeExportLink;
    if (r.error || !link) {
      tab?.close();
      message.error(describeError(r.error, 'تعذّر إنشاء الملف.'));
      return;
    }
    if (tab) tab.location.href = link;
    else window.location.href = link;
  }

  return (
    <Dropdown
      menu={{
        items: [
          { key: 'XLSX', label: 'Excel (.xlsx)' },
          { key: 'CSV', label: 'CSV' },
        ],
        onClick: ({ key }) => run(key as 'XLSX' | 'CSV'),
      }}
    >
      <Button icon={<DownloadOutlined />} loading={busy}>
        تصدير
      </Button>
    </Dropdown>
  );
}

/** One line: what (cut with «…», full text on hover), then its tags. */
function Description({ op }: { op: TableOperation }) {
  const text = op.description || op.category?.name || TYPE_LABELS[op.type];
  return (
    <div className="desk-description">
      <Tooltip
        title={
          op.needsReview && op.reviewNote
            ? `${text} — للمراجعة: ${op.reviewNote}`
            : text
        }
        placement="topRight"
      >
        <span className="desk-description-text">{text}</span>
      </Tooltip>
      {op.transferId && <Tag color="blue">تحويل</Tag>}
      {op.debtId && <Tag color="purple">قرض</Tag>}
      {op.source === 'ledger_import' && <Tag>مستورد</Tag>}
      {op.source === 'bank_statement' && <Tag color="blue">كشف بنكي</Tag>}
      {op.project?.status === 'CLOSED' && <Tag>مغلق</Tag>}
    </div>
  );
}

/** Cells hold their own controls: a click there doesn't open the row. */
function CellBox({ children }: { children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only stops the row's click
    // biome-ignore lint/a11y/useKeyWithClickEvents: the controls inside handle keys
    <div className="desk-cell" onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  );
}

/**
 * The table's changes before they are saved: each row and field, before →
 * after. «حفظ» sends them in bulk (one call per identical change); saved rows
 * leave the list, and the ones arqam refuses stay with the reason.
 */
function Changes({
  drafts,
  lists,
  onSaved,
  onClose,
}: {
  drafts: Drafts;
  lists: Lists | undefined;
  onSaved: (id: string) => void;
  onClose: () => void;
}) {
  const client = useClient();
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);
  const [failures, setFailures] = useState<Record<string, string>>({});
  const entries = Object.entries(drafts);

  const show = (key: keyof Draft, v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (key === 'needsReview') return v ? 'نعم' : 'لا';
    if (key === 'reviewNote') return String(v);
    const pool: ({ id: string; name: string } | null)[] | undefined =
      key === 'categoryId'
        ? lists?.operationCategories?.nodes
        : key === 'counterpartyId'
          ? lists?.counterparties?.nodes
          : lists?.projects?.nodes;
    return present(pool).find((x) => x.id === v)?.name ?? '؟';
  };

  // Rows with the same change go in one call (all or nothing). A row arqam
  // refuses is named in its error: it stays, the others are sent again.
  async function save() {
    setSaving(true);
    let saved = 0;
    const failed: Record<string, string> = {};
    const groups = groupByPatch(entries.map(([id, d]) => [id, d.patch]));
    for (const { patch, ids } of groups) {
      let left = ids;
      while (left.length) {
        const result = await client
          .mutation(
            UpdateOperationsDocument,
            { ids: left, patch },
            financeContext,
          )
          .toPromise();
        if (!result.error) {
          saved += left.length;
          for (const id of left) onSaved(id);
          break;
        }
        const { message: text, operationId } = bulkError(result.error);
        if (operationId && left.includes(operationId)) {
          failed[operationId] = text;
          left = left.filter((id) => id !== operationId);
        } else {
          for (const id of left) failed[id] = text;
          break;
        }
      }
    }
    setSaving(false);
    if (saved) message.success(`حُفظت ${operationsCount(saved)}`);
    setFailures(failed);
    if (!Object.keys(failed).length) onClose();
  }

  return (
    <Modal
      open
      width={760}
      title={`مراجعة التعديلات قبل الحفظ: ${operationsCount(entries.length)}`}
      okText="حفظ"
      cancelText="رجوع"
      confirmLoading={saving}
      okButtonProps={{ disabled: !entries.length }}
      onOk={save}
      onCancel={onClose}
    >
      {Object.keys(failures).length > 0 && (
        <Alert
          type="error"
          showIcon
          message="رفض الخادم بعض التعديلات: صحّحها في الجدول أو تراجع عنها."
        />
      )}
      <table className="desk-changes">
        <thead>
          <tr>
            <th>العملية</th>
            <th>الحقل</th>
            <th>قبل</th>
            <th>بعد</th>
          </tr>
        </thead>
        <tbody>
          {entries.flatMap(([id, { op, patch }]) =>
            (Object.keys(patch) as (keyof Draft)[]).map((key, i) => (
              <tr
                key={`${id}:${key}`}
                className={failures[id] ? 'is-failed' : undefined}
              >
                {i === 0 && (
                  <td rowSpan={Object.keys(patch).length}>
                    <div>{op.description || TYPE_LABELS[op.type]}</div>
                    <div className="field-hint ltr-num">
                      {op.date} · {dirhams(op.amount)}
                    </div>
                    {failures[id] && (
                      <div className="field-error">{failures[id]}</div>
                    )}
                  </td>
                )}
                <td>{FIELD_LABELS[key]}</td>
                <td className="is-before">
                  {show(key, op[key as keyof TableOperation])}
                </td>
                <td className="is-after">{show(key, patch[key])}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </Modal>
  );
}
